import { supabase, isConfigured } from './supabase';
import type { Deal } from '../types';

export interface CreateDealInput {
  team_id: string;
  contact_id?: string | null;
  owner_user_id?: string | null;
  title: string;
  stage?: string;
  value_monthly?: number | null;
  value_credits_est?: number | null;
  close_date?: string | null;
  next_follow_up?: string | null;
}

export interface UpdateDealInput extends Partial<Omit<CreateDealInput, 'team_id'>> {
  id: string;
}

export async function getDeals(
  teamId: string,
  options?: { allTeams?: boolean }
): Promise<Deal[]> {
  if (!isConfigured) return [];

  let query = supabase
    .from('sales_deals')
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company, email)
    `)
    .order('created_at', { ascending: false });

  if (!options?.allTeams) {
    query = query.eq('team_id', teamId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[deals] fetch failed:', error);
    return [];
  }

  return data as Deal[];
}

export async function getDeal(id: string): Promise<Deal | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_deals')
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company, email)
    `)
    .eq('id', id)
    .single();

  if (error) return null;
  return data as Deal;
}

export async function createDeal(input: CreateDealInput): Promise<Deal | null> {
  if (!isConfigured) return null;

  const { data, error } = await supabase
    .from('sales_deals')
    .insert(input)
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company, email)
    `)
    .single();

  if (error) {
    console.error('[deals] create failed:', error);
    return null;
  }

  return data as Deal;
}

export async function updateDeal(input: UpdateDealInput): Promise<Deal | null> {
  if (!isConfigured) return null;

  const { id, ...updates } = input;
  const { data, error } = await supabase
    .from('sales_deals')
    .update(updates)
    .eq('id', id)
    .select(`
      *,
      contact:sales_contacts(id, first_name, last_name, company, email)
    `)
    .single();

  if (error) {
    console.error('[deals] update failed:', error);
    return null;
  }

  return data as Deal;
}

export async function updateDealStage(id: string, stage: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_deals')
    .update({ stage })
    .eq('id', id);

  if (error) {
    console.error('[deals] update stage failed:', error);
    return false;
  }

  return true;
}

export async function deleteDeal(id: string): Promise<boolean> {
  if (!isConfigured) return false;

  const { error } = await supabase
    .from('sales_deals')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('[deals] delete failed:', error);
    return false;
  }

  return true;
}
